import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { fetch } from '@ngrx/router-store/data-persistence';
import {
  Observable,
  catchError,
  exhaustMap,
  map,
  mergeMap,
  of,
  withLatestFrom,
} from 'rxjs';

import * as UploadPictureActions from './upload-picture.actions';
import {
  DataAccessUploadService,
  IUploadService,
} from '@fantaz/shared/data-access/api';
import { CoreGoogleAnalyticActions } from '@fantaz/core/analytic';
import { MatDialog } from '@angular/material/dialog';
import { UploadImageDialogComponent } from '@fantaz/user-verification/ui/upload-image-dialog';
import {
  GAEventKeys,
  UserGameProfile,
} from '@fantaz/shared/data-access/models';
import { AuthSelectors } from '@fantaz/shared/auth';
import { ModalAlertComponent } from '@fantaz/shared/ui/modal-alert';
import * as VerifyUserActions from '../verify-user/verify-user.actions';

@Injectable()
export class UploadPictureEffects {
  openUploadPictureDialogEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadPictureActions.showUploadPictureDialog),
      exhaustMap(() => {
        this.dialog.closeAll();
        const dialogRef = this.dialog.open(UploadImageDialogComponent);
        return dialogRef.afterClosed();
      }),
      map((result: any) => {
        if (result?.source === 'button') {
          return VerifyUserActions.openVerificationDialog({});
        }
        return UploadPictureActions.closeUploadPictureDialog();
      }),
    ),
  );

  /**
   * Effect to handle upload picture to s3
   */
  handleUploadEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadPictureActions.uploadPicture),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      fetch({
        run: ({ file }, user) => {
          return this.uploadService.upload(file).pipe(
            mergeMap(() => {
              return [
                UploadPictureActions.uploadPictureSuccess(),
                CoreGoogleAnalyticActions.trackEvent({
                  event: {
                    event: GAEventKeys.USER_VERIFICATION_STATUS,
                    verification_action: 'user-upload-picture-success',
                    verification_status: `user-upload-picture-success`,
                    verification_attempts_failed:
                      user?.verificationStatus?.attempts?.current.DOCUMENT || 0,
                  },
                }),
              ];
            }),
            catchError((err) => {
              let error: string;
              if (err.message === 'Connection or timeout issue') {
                error =
                  'Connection or timeout issue. Please check your internet connection.';
              } else {
                error = 'Error uploading image: ' + err;
              }
              return [
                UploadPictureActions.uploadPictureFailure({ error }),
                CoreGoogleAnalyticActions.trackEvent({
                  event: {
                    event: GAEventKeys.USER_VERIFICATION_STATUS,
                    verification_action: 'user-upload-picture-failure',
                    verification_status: `user-upload-picture-failure`,
                    verification_attempts_failed:
                      user?.verificationStatus?.attempts?.current.DOCUMENT || 0,
                  },
                }),
              ];
            }),
          );
        },
      }),
    ),
  );

  /**
   * Effect to handle upload picture success
   */

  uploadPictureSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadPictureActions.uploadPictureSuccess),
      map(() => {
        return UploadPictureActions.verifyWithPicture();
      }),
    ),
  );

  verifyWithPicture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadPictureActions.verifyWithPicture),
      withLatestFrom(
        this.store.select(AuthSelectors.getAuthUser),
        (_, user) => ({
          user,
        }),
      ),
      mergeMap(({ user }) => {
        return this.uploadService
          .imageValidation({
            firstName: user?.firstName as string,
            lastName: user?.lastName as string,
            dateOfBirth: user?.birthday as string,
            email: user?.email as string,
            userUuid: user?.uuid as string,
            address: user?.legalAddress,
          } as UserGameProfile)
          .pipe(
            map((response) => {
              if (response.success) {
                return UploadPictureActions.verifyWithPictureSuccess();
              }
              return UploadPictureActions.verifyWithPictureFailure({
                error: response.clientMessage as string,
              });
            }),
            catchError((error) =>
              of(UploadPictureActions.verifyWithPictureFailure({ error })),
            ),
          );
      }),
    ),
  );

  verifyPictureSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UploadPictureActions.verifyWithPictureSuccess),
        exhaustMap(() => {
          this.dialog.closeAll();
          const dialogRef = this.dialog.open(ModalAlertComponent, {
            data: {
              modalType: 'IMAGE_VALIDATED',
            },
          });
          return dialogRef.afterClosed();
        }),
      ),
    { dispatch: false },
  );

  verifyWithPictureFailure$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadPictureActions.verifyWithPictureFailure),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      exhaustMap(([{ error }, user]) => {
        return [
          CoreGoogleAnalyticActions.trackEvent({
            event: {
              event: GAEventKeys.USER_VERIFICATION_STATUS,
              verification_action: 'user-upload-picture-fail',
              verification_status: error,
              verification_attempts_failed:
                user?.verificationStatus?.attempts?.current.DOCUMENT || 0,
            },
          }),
        ];
      }),
    ),
  );

  handleGAVerifyPictureSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadPictureActions.verifyWithPictureSuccess),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      exhaustMap(([action, user]) => {
        return [
          CoreGoogleAnalyticActions.trackEvent({
            event: {
              event: GAEventKeys.USER_VERIFICATION_STATUS,
              verification_action: 'user-upload-picture-success',
              verification_status: `user-upload-picture-success`,
              verification_attempts_failed:
                user?.verificationStatus?.attempts?.current.DOCUMENT || 0,
            },
          }),
        ];
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    @Inject(DataAccessUploadService)
    private readonly uploadService: IUploadService,
    private readonly dialog: MatDialog,
  ) {}
}
